import { Component, ElementRef, Input, OnDestroy, OnInit, QueryList, Signal, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { CustomerModel } from '../../../models/customer-model';
import { Navigator } from "../../menu/navigator";
import { LobbyNavigator } from '../../../services/lobby/lobby-navigator.service';
import { WebcontentService } from '../../../services/webcontent/webcontent.service';
import { LobbyInitService } from '../../../services/lobby/lobby-init.service';
import { MobileDataService } from '../../../services/mobile-data/mobile-data.service';
import { JurisdictionService } from '../../../services/geo/jurisdiction.service';
import { GeoResponse } from '../../../interfaces/geo-interface';
import { WebservicesService } from '../../../services/webservices/webservices.service';
import { UserSessionService } from '../../../services/user-session/user-session.service';
import { EventsService } from '../../../services/event/event.service';
import { debounceTime, Subscription } from 'rxjs';

@Component({
  selector: 'app-mobile-keywords',
  templateUrl: './mobile-keywords.component.html',
  styleUrls: ['./mobile-keywords.component.scss'],
})
export class MobileKeywordsComponent implements OnInit, OnDestroy {
  public activeKeyword: any; 
  public customer: CustomerModel;
  public keywordsSignal: Signal<any[]>;
  public keywords: any[] = [];
  public lobbyName: any;
  public keyword_route;
  public originalKeywords: any;
  public showSearchButton: boolean = true;
  public searchOpen: boolean = false;
  public tabCasinoClickedSubscription: Subscription;
  private keywordsSubscription: Subscription;
  private keywordClickedSubscription: Subscription;
  private highlightKeywordSubscription: Subscription;
  private isAuthenticated: boolean = false;

  @ViewChildren('keywordElement') keywordElements: QueryList<ElementRef>; // Get references to all keyword elements

  constructor(
    public router: Router,
    public activatedRoute: ActivatedRoute,
    public jursidiction: JurisdictionService,
    private lobbyInit: LobbyInitService,
    private lobbyNavigator: LobbyNavigator,
    private navigator: Navigator,
    private webservicesService: WebservicesService,
    private userSessionService: UserSessionService,
    private webContentService: WebcontentService,
    private mobileData: MobileDataService,
    private eventService: EventsService,
    private el: ElementRef,
  ) { }

  ngOnInit() {
    
    this.fetchKeywords();
   
    this.mobileData.getMobileLobbyKeyword().subscribe((keywordSubject) => {
      if(keywordSubject?.bottomKeyword){
        this.activeKeyword = this.keywords.find((k:any) => k.name == keywordSubject.bottomKeyword.name);
      }
      if(keywordSubject?.topKeyword){
        this.activeKeyword = this.keywords.find((k:any) => k.name == keywordSubject.topKeyword.name);
      }
      if (!this.activeKeyword) {
        this.activeKeyword = '';
      }
    });
    
    this.mobileData.getMobileCasinoDeeplink()
      .pipe(debounceTime(300))
      .subscribe((deeplink) => {
        if (deeplink) {
          this.checkDeepLinkKeyword(deeplink);
          this.mobileData.setMobileCasinoDeeplink(null);
      }
    });

    this.eventService.subscribe('keyword:click', (keyword) => {
      this.activeKeyword = keyword;
      this.onSelectKeyword(keyword);
    });
    
    this.userSessionService.getAuthenticationStatus().subscribe((status) => {
      this.isAuthenticated = status;
      this.getCustomer();
     });
  }

  private fetchKeywords() {
    this.keywordsSubscription = this.lobbyInit.keywords$.subscribe((currentKeywords) => {
      if (currentKeywords && currentKeywords.length > 0) {
        this.keywords = currentKeywords;
        
        // Check both the current value and subscription
        const currentKeyword = this.mobileData.getCurrentLobbyKeyword();
        if (!this.activeKeyword && currentKeyword) {
          if (currentKeyword.topKeyword) {
            this.activeKeyword = this.keywords.find((k: any) => k.name === currentKeyword.topKeyword.name);
          }
        } else if (!this.activeKeyword && this.keywords.length) {
          let currentActiveKeyWord = this.lobbyNavigator.getCurrentActiveKeywordItem();
          if (currentActiveKeyWord) {
            this.activeKeyword = currentActiveKeyWord;
            this.onSelectKeyword(this.activeKeyword);
          }
        }
      }
    });
  }

  private checkDeepLinkKeyword(data: string) {
    const keywordRouteKeys = ["keyword_route"];
    const actionParamKeys = ["action_param"];
    
    const keywordRouteMatches = this.keywords.filter(obj => 
      keywordRouteKeys.every(key => key in obj)
    );
    const actionParamMatches = this.keywords.filter(obj => 
      actionParamKeys.every(key => key in obj)
    );
    
    const hiddenKeywordRouteMatches = this.mobileData.getHiddenKeywordListMobile().filter(obj => 
      keywordRouteKeys.every(key => key in obj)
    );
  
    const matchedKeywordRoute = keywordRouteMatches.find(obj => 
      obj.keyword_route?.toLowerCase() === data.toLowerCase()
    );
    const matchedActionParam = actionParamMatches.find(obj => 
      obj.action_param?.toLowerCase() === data.toLowerCase()
    );
    const matchedHiddenKeywordRoute = hiddenKeywordRouteMatches.find(obj => 
      obj.keyword_route?.toLowerCase() === data.toLowerCase()
    );
  
    if (matchedKeywordRoute) {
      this.setDeeplinkKeyword(data, matchedKeywordRoute);
    } else if (matchedActionParam) {
      this.setDeeplinkKeyword(data, matchedActionParam);
    } else if (matchedHiddenKeywordRoute) {
      this.setDeeplinkKeyword(data, matchedHiddenKeywordRoute);
    }
  }
  
  private setDeeplinkKeyword(deeplink: string, keyword: any) {
    const specialKeywords = ["sports", "promos"];
    
    if (specialKeywords.includes(deeplink.toLowerCase())) {
      this.activeKeyword = this.keywords.find((k: any) => k.name === keyword.name);
    } else {
      this.mobileData.setMobileLobbyKeyword({ topKeyword: keyword });
    }
  }

  private scrollToActiveKeyword() {
    // Find the active element within the keyword elements and scroll to it
    const activeElement =
    this.el.nativeElement.querySelector(".keywords-isactive");
    if (activeElement) {
    const container =
      this.el.nativeElement.querySelector(".mobile-top-navbar");
        const containerLeft = container.scrollLeft;
        const containerRight = containerLeft + container.clientWidth;
        const elementLeft = activeElement.offsetLeft;
        const elementRight = elementLeft + activeElement.clientWidth;
        const containerOffset = container.getBoundingClientRect().left;
        const elementLeftWithOffset = elementLeft - containerOffset;
        const elementRightWithOffset = elementRight - containerOffset;

        if (elementLeftWithOffset < containerLeft) {
          container.scrollLeft = elementLeftWithOffset;
        } else if (elementRightWithOffset > containerRight) {
          container.scrollLeft = elementRightWithOffset - container.clientWidth;
        }
    }
  }

  public onClickKeyword(keyword: any): any {
    this.activeKeyword = keyword;
    this.onSelectKeyword(keyword);
  }

  public onSelectKeyword(keyword: any): any {
    this.mobileData.setMobileLobbyKeyword({topKeyword: keyword}); 
   
    setTimeout(() => {
      this.scrollToActiveKeyword();
    }, 100);
    const actionType = keyword?.action_type;
    const actionParam = keyword?.action_param;
    const code = keyword?.keyword_code;
   if (this.isAuthenticated) {
      const actionParamForGameList: any = actionParam;
      if (!!actionParamForGameList && actionParamForGameList == 'livedealer') {
        this.lobbyNavigator.setKeyword(keyword);
        return this.internalNavAction(keyword, actionParamForGameList);
      }
    }
    switch (actionType) {
      case 'externalpageload':
        this.webviewPageLoad(keyword);
        break;
      case 'tab':
        if (code?.toLowerCase()?.includes('sports') && this.isAuthenticated) {
          this.jursidiction.debounceVerifyJurisdiction(() => { }).then((geoResp: GeoResponse) => {
            this.navigator.toTab(actionParam);
          });
        } else {
          this.navigator.toTab(actionParam);
        }
        break;
      default:
        this.lobbyNavigator.setKeyword(keyword);
        this.activeKeyword = keyword;
        this.navigator.toCasino();
    }
  } 

 

  private internalNavAction(key: any, nav: any): void {

    switch (nav) {
      case 'livedealer':
        this.isAuthenticated ? this.loadLiveDealerAfterGeo() : this.navigator.toLiveDealer();
        break;
      default:
        this.navigator.toCasino();
        break;
    }
  }

  public loadLiveDealerAfterGeo(): any {

    return this.jursidiction.debounceVerifyJurisdiction(() => this.loadLiveDealerAfterGeo()).then((geoResp: GeoResponse) => {
      if (geoResp.is_allowed) {
        this.closeGamesBeforeLiveDealer();
      }
    }).catch(error => {
      console.error("Geo error", error);
      this.navigator.toCasino();
      this.lobbyNavigator.setKeyword(this.lobbyNavigator.getCasinoDefaultKeyword())
    });
  }

  private closeGamesBeforeLiveDealer() {
    let obj: any = {
      customer_id: this.customer.customerId,
      session_token: this.userSessionService.getSessionToken(),
      game_launcher_id: "-1",
    };

    this.webservicesService.post("games/close-game", obj).subscribe({
      next: () => {
        return this.navigator.toLiveDealer()
      }, error: error => {
        this.navigator.toLiveDealer();
      }
    });
  }
  private webviewPageLoad(key: any): any {
    const stub: string = key.action_param;
    this.webContentService.updateExternalLink(stub);
    this.navigator.toTab("c", false, stub);
  }

  public getCustomer() {
    this.userSessionService.getCustomerSubscription().then((customerSubscription) => {
      customerSubscription.subscribe((customer) => {
        if (customer) {
          this.customer = customer;
        }
      });
    });
  }
 

  ngOnDestroy() {
    this.tabCasinoClickedSubscription?.unsubscribe();
    this.keywordsSubscription?.unsubscribe();
    this.keywordClickedSubscription?.unsubscribe();
    this.highlightKeywordSubscription?.unsubscribe();
  }

  }